import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Stack,
  Avatar,
  Divider,
  Autocomplete,
  Chip,
  Card,
  CardContent,
  Grid,
  Tooltip,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import SmsIcon from "@mui/icons-material/Sms";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import ArticleIcon from "@mui/icons-material/Article";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import {
  getSmsAddressBook,
  getSmsTemplateList,
  sendSmsOutbound,
  sendSmsOutboundSuccess,
  addSmsTemplate,
  addSmsTemplateSuccess,
} from "../../../store/sms/action";

import AppModal from "../../Common/AppModal";

const DEFAULT_FROM_NAME = "ESCIT";

const getContactPhone = (option) =>
  option?.ContactPersonTelephoneNumber_TelephoneNumber ||
  option?.TelephoneNumber ||
  option?.MobileNumber ||
  "";

const getContactName = (option) => {
  if (!option) return "";

  const firstLast = `${option?.ContactPerson_FirstName || ""} ${
    option?.ContactPerson_LastName || ""
  }`.trim();

  return option?.ContactPerson_FullName || firstLast || option?.FullName || "";
};

const getContactCustomer = (option) =>
  option?.Customer_FullName ||
  option?.CustomerFullName ||
  option?.Customer_Name ||
  "";

const getContactPersonId = (option) =>
  option?.ContactPerson_Id || option?.ContactPersonId || null;

const getContactCustomerId = (option) =>
  option?.Customer_Id || option?.CustomerId || null;

const getContactLabel = (option) => {
  if (!option) return "";

  const name = getContactName(option);
  const customer = getContactCustomer(option);
  const phone = getContactPhone(option);

  return [name, customer, phone].filter(Boolean).join(" - ");
};

const getAddressBookOptions = (smsAddressBook) => {
  if (Array.isArray(smsAddressBook)) return smsAddressBook;

  if (Array.isArray(smsAddressBook?.contactperson_list)) {
    return smsAddressBook.contactperson_list;
  }

  if (Array.isArray(smsAddressBook?.customercontactperson_list)) {
    return smsAddressBook.customercontactperson_list;
  }

  if (Array.isArray(smsAddressBook?.data)) {
    return smsAddressBook.data;
  }

  return [];
};

const SmsTemplatePickerDialog = ({
  open,
  onClose,
  templates,
  loading,
  onSelectTemplate,
  onTemplateCreated,
}) => {
  const dispatch = useDispatch();

  const { addSmsTemplateLoading, addSmsTemplateResponse } = useSelector(
    (state) => ({
      addSmsTemplateLoading: state.SmsReducer?.addSmsTemplateLoading,
      addSmsTemplateResponse: state.SmsReducer?.addSmsTemplate,
    })
  );

  const [templateSearch, setTemplateSearch] = useState("");
  const [createMode, setCreateMode] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newBody, setNewBody] = useState("");

  const filteredTemplates = useMemo(() => {
    const query = templateSearch?.trim()?.toLowerCase();
    if (!query) return templates || [];
    return (templates || []).filter((template) => {
      const title = template?.Title?.toLowerCase() || "";
      const body = template?.Body?.toLowerCase() || "";
      return title.includes(query) || body.includes(query);
    });
  }, [templates, templateSearch]);

  useEffect(() => {
    if (open) {
      setTemplateSearch("");
      setCreateMode(false);
      setNewTitle("");
      setNewBody("");
      dispatch(addSmsTemplateSuccess({}));
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (addSmsTemplateResponse?.status === 200) {
      toast.success(
        addSmsTemplateResponse?.message || "Template created successfully"
      );
      dispatch(addSmsTemplateSuccess({}));
      setCreateMode(false);
      setNewTitle("");
      setNewBody("");
      if (onTemplateCreated) onTemplateCreated();
    } else if (addSmsTemplateResponse?.status && addSmsTemplateResponse?.status !== 200) {
      toast.error(addSmsTemplateResponse?.message || "Failed to create template");
      dispatch(addSmsTemplateSuccess({}));
    }
  }, [addSmsTemplateResponse, open]);

  const handleSaveTemplate = () => {
    if (!newTitle.trim()) {
      toast.error("Please enter a template title");
      return;
    }
    if (!newBody.trim()) {
      toast.error("Please enter a template message");
      return;
    }
    dispatch(addSmsTemplate([{ Title: newTitle.trim(), Body: newBody.trim() }]));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          background:
            "linear-gradient(135deg, rgba(25,118,210,0.12), rgba(25,118,210,0.03))",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          {createMode && (
            <IconButton onClick={() => setCreateMode(false)} size="small">
              <ArrowBackIcon />
            </IconButton>
          )}

          <Avatar sx={{ bgcolor: "primary.main", width: 44, height: 44 }}>
            {createMode ? <AddIcon /> : <ArticleIcon />}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DialogTitle sx={{ p: 0, fontWeight: 700, fontSize: 20 }}>
              {createMode ? "New SMS Template" : "Choose SMS Template"}
            </DialogTitle>

            <Typography variant="body2" color="text.secondary">
              {createMode
                ? "Save a reusable message to use next time."
                : "Pick one of your saved SMS templates to insert into the message."}
            </Typography>
          </Box>

          {!createMode && (
            <Tooltip title="Create a new template">
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateMode(true)}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
              >
                New Template
              </Button>
            </Tooltip>
          )}

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {createMode ? (
          <Stack spacing={2.5}>
            <TextField
              label="Template Title"
              placeholder="e.g. Appointment Reminder"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              fullWidth
              disabled={addSmsTemplateLoading}
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              label="Message Body"
              placeholder="Type your template message..."
              value={newBody}
              onChange={(e) => setNewBody(e.target.value)}
              fullWidth
              multiline
              minRows={5}
              disabled={addSmsTemplateLoading}
              inputProps={{ maxLength: 320 }}
              helperText={`${newBody.length}/320 characters`}
            />
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            <TextField
              label="Search templates"
              placeholder="Search by title or message body..."
              value={templateSearch}
              onChange={(e) => setTemplateSearch(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ArticleIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {loading ? (
              <Box sx={{ py: 6, display: "flex", justifyContent: "center", alignItems: "center" }}>
                <CircularProgress />
              </Box>
            ) : filteredTemplates?.length > 0 ? (
              <Grid container spacing={2}>
                {filteredTemplates.map((template) => {
                  const body = template?.Body || "";
                  const preview = body.length > 180 ? `${body.slice(0, 180)}...` : body;

                  return (
                    <Grid item xs={12} md={6} key={template?.Id}>
                      <Card
                        onClick={() => onSelectTemplate(template)}
                        sx={{
                          height: "100%",
                          cursor: "pointer",
                          borderRadius: 3,
                          border: "1px solid",
                          borderColor: "divider",
                          boxShadow: "0 8px 22px rgba(15, 23, 42, 0.06)",
                          transition: "all 0.18s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            borderColor: "primary.main",
                            boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
                          },
                        }}
                      >
                        <CardContent>
                          <Stack spacing={1.2}>
                            <Stack direction="row" alignItems="flex-start" spacing={1}>
                              <Avatar sx={{ width: 34, height: 34, bgcolor: "primary.light", color: "primary.contrastText" }}>
                                <SmsIcon fontSize="small" />
                              </Avatar>

                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle2" fontWeight={800} noWrap>
                                  {template?.Title || "Untitled Template"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {body.length || 0} characters
                                </Typography>
                              </Box>

                              <Tooltip title="Use this template">
                                <ContentCopyIcon fontSize="small" color="action" />
                              </Tooltip>
                            </Stack>

                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
                              {preview || "No message body."}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Box
                sx={{
                  py: 6,
                  textAlign: "center",
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 3,
                  bgcolor: "grey.50",
                }}
              >
                <ArticleIcon color="disabled" sx={{ fontSize: 42, mb: 1 }} />
                <Typography fontWeight={700}>No SMS templates found</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Create one now or manage all templates in SMS Settings.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateMode(true)}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                >
                  Create New Template
                </Button>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, bgcolor: "grey.50", justifyContent: "space-between" }}>
        <Button
          size="small"
          color="inherit"
          endIcon={<OpenInNewIcon fontSize="small" />}
          onClick={() => { window.location.href = "/settings/1/39"; }}
          sx={{ borderRadius: 2, textTransform: "none", fontSize: 12 }}
        >
          Manage in Settings
        </Button>

        <Stack direction="row" spacing={1}>
          {createMode ? (
            <>
              <Button onClick={() => setCreateMode(false)} color="inherit" sx={{ borderRadius: 2 }} disabled={addSmsTemplateLoading}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveTemplate}
                disabled={addSmsTemplateLoading || !newTitle.trim() || !newBody.trim()}
                startIcon={addSmsTemplateLoading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
              >
                {addSmsTemplateLoading ? "Saving..." : "Save Template"}
              </Button>
            </>
          ) : (
            <Button onClick={onClose} color="inherit" sx={{ borderRadius: 2 }}>
              Close
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

const SendSms = ({
  modal,
  toggle,
  phoneNumber: initialPhoneNumber = "",
  fromName = DEFAULT_FROM_NAME,
}) => {
  const dispatch = useDispatch();

  const {
    smsAddressBook,
    smsAddressBookLoading,
    sendSmsOutboundResponse,
    sendSmsOutboundLoading,
    smsTemplateList,
    smsTemplateListLoading,
  } = useSelector((state) => ({
    smsAddressBook: state.SmsReducer?.smsAddressBook,
    smsAddressBookLoading: state.SmsReducer?.smsAddressBookLoading,

    sendSmsOutboundResponse: state.SmsReducer?.sendSmsOutbound,
    sendSmsOutboundLoading: state.SmsReducer?.sendSmsOutboundLoading,

    smsTemplateList: state.SmsReducer?.smsTemplateList?.smstemplate_list || [],
    smsTemplateListLoading: state.SmsReducer?.smsTemplateListLoading,
  }));

  const [selectedContact, setSelectedContact] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [localAddressBookOptions, setLocalAddressBookOptions] = useState([]);

  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber || "");
  const [phoneLocked, setPhoneLocked] = useState(false);
  const [message, setMessage] = useState("");
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  const addressBookOptions = useMemo(() => {
    if (!searchValue?.trim() || searchValue.trim().length < 2) {
      return [];
    }

    return localAddressBookOptions;
  }, [localAddressBookOptions, searchValue]);

  useEffect(() => {
    if (modal) {
      setPhoneNumber(initialPhoneNumber || "");
      setSelectedContact(null);
      setPhoneLocked(false);
      setSearchValue("");
      setLocalAddressBookOptions([]);
      setMessage("");
      setTemplatePickerOpen(false);

      dispatch(sendSmsOutboundSuccess({}));
      dispatch(getSmsTemplateList());
    }
  }, [modal, initialPhoneNumber, dispatch]);

  useEffect(() => {
    if (!modal) return;

    const query = searchValue?.trim();

    if (!query || query.length < 2) {
      setLocalAddressBookOptions([]);
      return;
    }

    setLocalAddressBookOptions([]);

    const delayDebounce = setTimeout(() => {
      dispatch(
        getSmsAddressBook({
          QueryValue: query,
        })
      );
    }, 350);

    return () => clearTimeout(delayDebounce);
  }, [searchValue, modal, dispatch]);

  useEffect(() => {
    if (!modal) return;

    const query = searchValue?.trim();

    if (!query || query.length < 2) {
      setLocalAddressBookOptions([]);
      return;
    }

    setLocalAddressBookOptions(getAddressBookOptions(smsAddressBook));
  }, [smsAddressBook, searchValue, modal]);

  useEffect(() => {
    if (isEmpty(sendSmsOutboundResponse)) return;

    if (sendSmsOutboundResponse?.status === 200) {
      toast.success(
        sendSmsOutboundResponse?.message || "SMS sent successfully"
      );

      setMessage("");
      setPhoneNumber("");
      setSelectedContact(null);
      setPhoneLocked(false);
      setSearchValue("");
      setLocalAddressBookOptions([]);
      setTemplatePickerOpen(false);

      dispatch(sendSmsOutboundSuccess({}));
      toggle();
    } else {
      toast.error(sendSmsOutboundResponse?.message || "Failed to send SMS");
      dispatch(sendSmsOutboundSuccess({}));
    }
  }, [sendSmsOutboundResponse, dispatch, toggle]);

  const handleClose = () => {
    if (!sendSmsOutboundLoading) {
      toggle();
    }
  };

  const handleContactSelect = (event, option) => {
    setSelectedContact(option);

    if (!option) {
      setPhoneNumber("");
      setPhoneLocked(false);
      return;
    }

    const label = getContactLabel(option);
    const phone = getContactPhone(option);

    setSearchValue(label);

    if (phone) {
      setPhoneNumber(phone);
      setPhoneLocked(true);
    }
  };

  const handleOpenTemplates = () => {
    setTemplatePickerOpen(true);

    if (!smsTemplateList?.length) {
      dispatch(getSmsTemplateList());
    }
  };

  const handleSelectTemplate = (template) => {
    setMessage(template?.Body || "");
    setTemplatePickerOpen(false);
  };

  const handleSend = () => {
    const trimmedPhone = phoneNumber?.trim();
    const trimmedMessage = message?.trim();

    if (!trimmedPhone) {
      toast.error("Please enter a phone number");
      return;
    }

    if (!trimmedMessage) {
      toast.error("Please enter a message");
      return;
    }

    const payload = {
      FromName: fromName,
      MobileNumber: trimmedPhone,
      Message: trimmedMessage,
    };

    if (selectedContact) {
      const customerId = getContactCustomerId(selectedContact);
      const contactPersonId = getContactPersonId(selectedContact);
      if (customerId) payload.Customer_Id = Number(customerId);
      if (contactPersonId) payload.ContactPerson_Id = Number(contactPersonId);
    }

    dispatch(sendSmsOutbound([payload]));
  };

  return (
    <>
      <SmsTemplatePickerDialog
        open={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        templates={smsTemplateList}
        loading={smsTemplateListLoading}
        onSelectTemplate={handleSelectTemplate}
        onTemplateCreated={() => dispatch(getSmsTemplateList())}
      />

      <AppModal
        open={modal}
        onClose={handleClose}
        title="Send SMS"
        subtitle="Search a contact, choose a template, or enter a message manually."
        icon={<SmsIcon />}
        headerAccent="indigo"
        size="sm"
        loading={sendSmsOutboundLoading}
        loadingText="Sending…"
        submitLabel="Send SMS"
        onSubmit={handleSend}
      >
        <Stack spacing={2.5}>
            <Autocomplete
              value={selectedContact}
              inputValue={searchValue}
              options={addressBookOptions}
              loading={smsAddressBookLoading}
              filterOptions={(options) => options}
              getOptionLabel={getContactLabel}
              isOptionEqualToValue={(option, value) =>
                option?.ContactPerson_Id === value?.ContactPerson_Id &&
                getContactPhone(option) === getContactPhone(value)
              }
              onChange={handleContactSelect}
              onInputChange={(event, value, reason) => {
                if (reason === "input") {
                  setSearchValue(value);
                  setSelectedContact(null);
                  setPhoneNumber("");
                  setLocalAddressBookOptions([]);
                }

                if (reason === "clear") {
                  setSearchValue("");
                  setSelectedContact(null);
                  setPhoneNumber("");
                  setPhoneLocked(false);
                  setLocalAddressBookOptions([]);
                }
              }}
              noOptionsText={
                smsAddressBookLoading
                  ? "Searching contacts..."
                  : searchValue?.trim()?.length < 2
                  ? "Type at least 2 characters to search"
                  : "No contacts found"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search contact"
                  placeholder="Search by name, company, or number..."
                  fullWidth
                  disabled={sendSmsOutboundLoading}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <PersonSearchIcon color="action" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {smsAddressBookLoading ? (
                          <CircularProgress size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                const name = getContactName(option);
                const customer = getContactCustomer(option);
                const phone = getContactPhone(option);

                return (
                  <Box component="li" {...props}>
                    <Stack spacing={0.4} sx={{ width: "100%" }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon fontSize="small" color="action" />

                        <Typography variant="body2" fontWeight={700}>
                          {name || "Unnamed contact"}
                        </Typography>
                      </Stack>

                      {customer && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <BusinessIcon fontSize="small" color="action" />

                          <Typography variant="caption" color="text.secondary">
                            {customer}
                          </Typography>
                        </Stack>
                      )}

                      {phone && (
                        <Chip
                          size="small"
                          icon={<PhoneIphoneIcon />}
                          label={phone}
                          sx={{
                            alignSelf: "flex-start",
                            mt: 0.5,
                          }}
                        />
                      )}
                    </Stack>
                  </Box>
                );
              }}
            />

            <TextField
              label="Phone Number"
              placeholder="Enter phone number..."
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              fullWidth
              disabled={sendSmsOutboundLoading}
              inputProps={{ readOnly: phoneLocked }}
              helperText={
                phoneLocked && selectedContact ? (
                  <Box
                    component="span"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <LockIcon sx={{ fontSize: 11 }} />
                    <span>
                      Sending to{" "}
                      <strong>{getContactName(selectedContact)}</strong>
                      {getContactCustomer(selectedContact)
                        ? ` · ${getContactCustomer(selectedContact)}`
                        : ""}
                      {getContactPersonId(selectedContact)
                        ? ` (Contact #${getContactPersonId(selectedContact)})`
                        : ""}
                    </span>
                  </Box>
                ) : null
              }
              sx={{
                "& .MuiOutlinedInput-root": phoneLocked
                  ? {
                      bgcolor: "rgba(25, 118, 210, 0.04)",
                      "& fieldset": { borderColor: "primary.main" },
                    }
                  : {},
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {phoneLocked ? (
                      <LockIcon color="primary" fontSize="small" />
                    ) : (
                      <PhoneIphoneIcon color="action" />
                    )}
                  </InputAdornment>
                ),
                endAdornment: phoneLocked ? (
                  <InputAdornment position="end">
                    <Tooltip title="Unlock to type a number manually">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setPhoneLocked(false);
                          setSelectedContact(null);
                        }}
                        disabled={sendSmsOutboundLoading}
                      >
                        <LockOpenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  Message
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  Write manually or insert a saved template.
                </Typography>
              </Box>

              <Button
                size="small"
                variant="outlined"
                startIcon={<ArticleIcon />}
                onClick={handleOpenTemplates}
                disabled={sendSmsOutboundLoading}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                Use Template
              </Button>
            </Box>

            <TextField
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              fullWidth
              multiline
              minRows={5}
              disabled={sendSmsOutboundLoading}
              inputProps={{
                maxLength: 320,
              }}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: -1,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Keep it short and clear for best delivery.
              </Typography>

              <Typography
                variant="caption"
                color={
                  message.length >= 300 ? "warning.main" : "text.secondary"
                }
              >
                {message.length}/320
              </Typography>
            </Box>
          </Stack>
      </AppModal>
    </>
  );
};

export default SendSms;
